import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect, useCallback } from "react";
import {
  fetchVendorAppointments,
  fetchVendorAppointmentFilterOptions,
  fetchActiveIndividuals,
  fetchActiveSubs,
  createAppointment,
  setAssignee,
  setDateFrom,
  setDateTo,
  setPage,
  setPageSize,
  setSearch,
  setServiceParent,
  setSortKey,
  setStatus,
  updateAppointmentInList,
  openSheet,
  closeSheet,
} from "./appointments.slice";
import { CreateAppointmentPayload } from "@/app/pmc/appointments/appointments.entity.ts";
import supabase from "@/lib/supabase";
import { useDebounce } from "@/hooks/useDebounce";

export const useVendorAppointments = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.vendorAppointments);

  // Debounce search input by 400ms to prevent excessive API calls
  // Keep Redux search updating immediately for responsive input display
  // Pass debounced value directly to fetch - no sync needed, prevents blocking input
  const debouncedSearch = useDebounce(state.filters.search, 400);

  useEffect(() => {
    const listing = dispatch(fetchVendorAppointments(debouncedSearch));
    listing.unwrap().catch((error) => {
      if (error.name !== "AbortError") console.error("Failed to fetch appointments:", error);
    });
    return () => listing.abort();
  }, [
    dispatch,
    state.filters.status,
    state.filters.serviceParent,
    state.filters.assignee,
    debouncedSearch, // Use debounced search - fetch only triggers after user stops typing
    state.filters.dateFrom,
    state.filters.dateTo,
    state.filters.sortKey,
    state.filters.sortOrder,
    state.pagination.page,
    state.pagination.pageSize,
  ]);

  useEffect(() => {
    dispatch(fetchVendorAppointmentFilterOptions());
    dispatch(fetchActiveIndividuals());
    dispatch(fetchActiveSubs());
  }, [dispatch]);

  // Memoize filter setters to prevent unnecessary re-renders
  const setStatusCallback = useCallback((v: string) => dispatch(setStatus(v)), [dispatch]);
  const setServiceParentCallback = useCallback((v: string) => dispatch(setServiceParent(v)), [dispatch]);
  const setAssigneeCallback = useCallback((v: string) => dispatch(setAssignee(v)), [dispatch]);
  const setSearchCallback = useCallback((v: string) => dispatch(setSearch(v)), [dispatch]);
  const setDateFromCallback = useCallback((v: string) => dispatch(setDateFrom(v)), [dispatch]);
  const setDateToCallback = useCallback((v: string) => dispatch(setDateTo(v)), [dispatch]);
  const setSortKeyCallback = useCallback((k: string) => dispatch(setSortKey(k)), [dispatch]);
  const setPageCallback = useCallback((p: number) => dispatch(setPage(p)), [dispatch]);
  const setPageSizeCallback = useCallback((ps: number) => dispatch(setPageSize(ps)), [dispatch]);
  const createAppointmentCallback = useCallback(
    (payload: Omit<CreateAppointmentPayload, "vendor">) => dispatch(createAppointment(payload)).unwrap(),
    [dispatch]
  );

  const openSheetCallback = useCallback(() => dispatch(openSheet()), [dispatch]);
  const closeSheetCallback = useCallback(() => dispatch(closeSheet()), [dispatch]);
  const refetchCallback = useCallback(() => dispatch(fetchVendorAppointments(undefined)), [dispatch]);
  const updateAppointmentInListCallback = useCallback((appointment: any) => dispatch(updateAppointmentInList(appointment)), [dispatch]);

  return {
    list: state.list,
    total: state.total,
    loading: state.loading,
    options: state.options,
    individuals: state.individuals,
    subs: state.subs,
    subsLoading: state.subsLoading,
    sheetOpen: state.sheetOpen,
    creating: state.creating,

    filters: {
      ...state.filters,
      setStatus: setStatusCallback,
      setServiceParent: setServiceParentCallback,
      setAssignee: setAssigneeCallback,
      setSearch: setSearchCallback,
      setDateFrom: setDateFromCallback,
      setDateTo: setDateToCallback,
      setSortKey: setSortKeyCallback,
    },

    pagination: {
      ...state.pagination,
      setPage: setPageCallback,
      setPageSize: setPageSizeCallback,
    },

    // actions
    create: createAppointmentCallback,
    openSheet: openSheetCallback,
    closeSheet: closeSheetCallback,
    refetch: refetchCallback,
    updateAppointmentInList: updateAppointmentInListCallback,

    dispatch,
  };
};
